import { datadogLogs } from "@datadog/browser-logs";
import type { IntakeFormToQuestion } from "@frontend/lib/graphql/generated-gql";
import { PDFDocument } from "pdf-lib";
import generatePDF from "react-to-pdf";

interface PageDimensions {
  width: number;
  height: number;
}

interface PDFGenerationOptions {
  orientation?: "portrait" | "landscape";
  margin?: number;
}

const LETTER_DIMENSIONS = {
  portrait: {
    width: 816, // 8.5 inches = 816px
    height: 1056, // 11 inches = 1056px
  },
  landscape: {
    width: 1056,
    height: 816,
  },
};

interface ElementDimensions {
  width: number;
  height: number;
}

const sanitizeHtml = (html: string) => {
  if (html.includes("<script>")) {
    datadogLogs.logger.error("XSS attack detected", {});
    return null;
  }

  return html;
};

function cloneAndAdjustInputElement(
  element: HTMLElement,
  intakeQuestionMap: Map<
    number,
    IntakeFormToQuestion & { originalText: string; originalAttributes: any }
  >,
): HTMLElement {
  // Create deep clone
  const clone = element.cloneNode(true) as HTMLElement;

  // If the input is a text ipnut
  if (clone.getAttribute("data-pdflabel") === "text-label") {
    clone.style.top = "-25px"; // Increase the negative top value
    clone.style.position = "absolute";

    const questionId = Number.parseInt(
      clone.getAttribute("data-questionid") as string,
    );
    const originalText = intakeQuestionMap.get(questionId)?.originalText;

    if (originalText) {
      clone.textContent = originalText;
    }
  }

  /**
   * This is so long due to how text inputs are formatted
   * as pdfs - it cuts of the text so I have to manually calculate the size of the input and add sizes
   */
  if (clone.getAttribute("data-pdflabel") === "text-input") {
    const originalStyle = window.getComputedStyle(element);

    const measureDiv = document.createElement("div");
    measureDiv.style.position = "absolute";
    measureDiv.style.visibility = "hidden";
    measureDiv.style.whiteSpace = "pre";
    measureDiv.style.font = originalStyle.font;
    measureDiv.textContent =
      (element as HTMLInputElement).value ||
      (element as HTMLInputElement).placeholder ||
      "Mg";
    document.body.appendChild(measureDiv);

    const textWidth = measureDiv.offsetWidth;
    const textHeight = measureDiv.offsetHeight;

    const padding = {
      left: Number.parseFloat(originalStyle.paddingLeft),
      right: Number.parseFloat(originalStyle.paddingRight),
      top: Number.parseFloat(originalStyle.paddingTop),
      bottom: Number.parseFloat(originalStyle.paddingBottom),
    };

    const border = {
      left: Number.parseFloat(originalStyle.borderLeftWidth),
      right: Number.parseFloat(originalStyle.borderRightWidth),
      top: Number.parseFloat(originalStyle.borderTopWidth),
      bottom: Number.parseFloat(originalStyle.borderBottomWidth),
    };

    const totalWidth =
      textWidth +
      padding.left +
      padding.right +
      border.left +
      border.right +
      20;
    const totalHeight = Math.max(textHeight * 2, 24);

    clone.style.width = `${totalWidth}px`;
    clone.style.height = `${totalHeight}px`;
    clone.style.lineHeight = `${totalHeight}px`;
    clone.style.verticalAlign = "middle";
    clone.style.boxSizing = "border-box";
    clone.style.overflow = "hidden";
    clone.style.transform = "translateY(-25%)";
    clone.style.position = "relative";

    // Make it look like a static text element
    clone.style.border = originalStyle.border;
    clone.style.backgroundColor = originalStyle.backgroundColor;
    clone.style.appearance = "none";
    clone.style.outline = "none";

    document.body.removeChild(measureDiv);
  }

  /**
   *  input label and choices
   */
  if (
    clone.getAttribute("data-pdflabel") === "singleInput-label" ||
    clone.getAttribute("data-pdflabel") === "multiInput-label" ||
    clone.getAttribute("data-pdflabel") === "signatureInput-label" ||
    clone.getAttribute("data-pdflabel") === "image-label"
  ) {
    const questionId = Number.parseInt(
      clone.getAttribute("data-questionid") as string,
    );

    const originalText = intakeQuestionMap.get(questionId)?.originalText;

    if (originalText) {
      clone.textContent = originalText;
    }
  }

  /**
   *  blurb for signature
   */
  if (clone.getAttribute("data-pdflabel") === "signatureInput-blurb") {
    const questionId = Number.parseInt(
      clone.getAttribute("data-questionid") as string,
    );

    const originalText =
      intakeQuestionMap.get(questionId)?.originalAttributes.blurb;

    if (originalText) {
      clone.innerHTML = sanitizeHtml(originalText) ?? "";
    }
  }

  if (
    clone.getAttribute("data-pdflabel") === "singleInput-choice" ||
    clone.getAttribute("data-pdflabel") === "multiInput-choice"
  ) {
    const questionId = Number.parseInt(
      clone.getAttribute("data-questionid") as string,
    );

    const choiceIndex = Number.parseInt(
      clone.getAttribute("data-choiceindex") as string,
    );

    const choices =
      intakeQuestionMap.get(questionId)?.originalAttributes.choices;

    if (choices) {
      clone.textContent = choices[choiceIndex].value;
    }
  }

  // Recursively handle child elements
  for (const child of Array.from(clone.children)) {
    if (child instanceof HTMLElement) {
      const adjustedChild = cloneAndAdjustInputElement(
        child,
        intakeQuestionMap,
      );
      child.parentElement?.replaceChild(adjustedChild, child);
    }
  }

  return clone;
}

class PDFPageGenerator {
  private readonly PAGE_MARGIN: number;
  private readonly dimensions: PageDimensions;
  private currentPage: HTMLDivElement | null;
  private currentPageHeight: number;
  private pages: HTMLDivElement[];

  constructor(
    private orientation: "portrait" | "landscape" = "portrait",
    margin = 40,
  ) {
    this.PAGE_MARGIN = margin;
    this.dimensions = LETTER_DIMENSIONS[orientation];
    this.currentPage = null;
    this.currentPageHeight = 0;
    this.pages = [];
  }

  private createNewPage(): HTMLDivElement {
    const page = document.createElement("div");
    page.style.width = `${this.dimensions.width}px`;
    page.style.height = `${this.dimensions.height}px`;
    page.style.padding = `${this.PAGE_MARGIN}px`;
    page.style.boxSizing = "border-box";
    page.style.position = "relative";
    page.style.backgroundColor = "white";
    page.style.overflow = "hidden";
    // Hide the page but keep it in document flow for proper rendering
    page.style.position = "absolute";
    page.style.left = "-9999px";
    document.body.appendChild(page);
    return page;
  }

  private async measureElementHeight(element: HTMLElement): Promise<number> {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.width = `${this.dimensions.width - this.PAGE_MARGIN * 2}px`;
    clone.style.position = "absolute";
    clone.style.left = "-9999px";
    clone.style.visibility = "hidden";
    document.body.appendChild(clone);

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        const height = clone.offsetHeight;
        document.body.removeChild(clone);
        resolve(height);
      });
    });
  }

  private initNewPage(): void {
    this.currentPage = this.createNewPage();
    this.currentPageHeight = 0;
    this.pages.push(this.currentPage);
  }

  public async distributeElements(
    elements: HTMLElement[],
    intakeQuestionMap: Map<number, any>,
  ): Promise<HTMLDivElement[]> {
    // Adjust input elements first

    const adjustedElements = elements.map((element) =>
      cloneAndAdjustInputElement(element, intakeQuestionMap),
    );

    const workingHeight = this.dimensions.height - this.PAGE_MARGIN * 4;
    this.initNewPage();

    for (const element of adjustedElements) {
      const elementHeight = await this.measureElementHeight(element);

      if (
        !this.currentPage ||
        this.currentPageHeight + elementHeight > workingHeight
      ) {
        this.initNewPage();
      }

      const elementClone = element.cloneNode(true) as HTMLElement;
      this.currentPage?.appendChild(elementClone);
      this.currentPageHeight += elementHeight;
    }

    return this.pages;
  }

  public cleanup(): void {
    for (const page of this.pages) {
      if (page.parentNode) {
        page.parentNode.removeChild(page);
      }
    }
  }
}

async function generateSinglePagePDF(
  element: HTMLElement,
  options: PDFGenerationOptions = {},
): Promise<ArrayBuffer> {
  const pdf = await generatePDF(() => element, {
    method: "build",
    page: {
      margin: options.margin ?? 10,
      format: "letter",
      orientation: options.orientation ?? "portrait",
    },
    canvas: {
      mimeType: "image/jpeg",
      qualityRatio: 0.5,
    },
  });

  return pdf.output("arraybuffer");
}

/**
 * This is an enormous function - let me explain why by dumping some alternatives to this approach
 *
 * 1. Use another hidden intake form like the printable version
 * - Does not work because some questions answers are saved by strings themselves ( multiSelect / singleSelect),
 * which means unless we defined how CHOICES are saved, we cannot define them to be the same answers in different languages
 *
 * 2. Set the user language to english, then capture the dom, then change it back
 * - Not only does this suffer the same problem as the previous approach, its much worse UX because it flickers the form in a different language
 *
 * This leads to an immediate solve for me using some very very declarative dom operation relying on
 * properties of each of the individual html elements
 *
 * ---
 * Besides, react-to-pdf loses some information in the conversion process if not handled manually
 * - Radio input save state
 *
 * @param elements
 * @param intakeQuestionMap
 * @param options
 * @returns
 */
export async function generateMultiPagePDF(
  elements: HTMLElement[],
  intakeQuestionMap: Map<number, any>,
  options: PDFGenerationOptions = {},
): Promise<ArrayBuffer> {
  try {
    // 1. Create page generator and distribute elements
    const pageGenerator = new PDFPageGenerator(
      options.orientation,
      options.margin,
    );

    const pageElements = await pageGenerator.distributeElements(
      elements,
      intakeQuestionMap,
    );

    // 2. Generate individual PDFs
    const pdfBuffers: ArrayBuffer[] = [];
    for (const pageElement of pageElements) {
      const pdfBuffer = await generateSinglePagePDF(pageElement, options);
      pdfBuffers.push(pdfBuffer);
    }

    // 3. Combine PDFs using pdf-lib
    const mergedPdf = await PDFDocument.create();

    for (const pdfBuffer of pdfBuffers) {
      const pdf = await PDFDocument.load(pdfBuffer);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      for (const page of copiedPages) {
        mergedPdf.addPage(page);
      }
    }

    // 4. Cleanup temporary elements
    pageGenerator.cleanup();

    // 5. Return the final PDF as ArrayBuffer
    return await mergedPdf.save();
  } catch (error) {
    console.error("PDF generation failed:", error);
    throw error;
  }
}
